import React, { type ComponentProps, useMemo } from 'react';
import { Link } from 'expo-router';
import { Pressable, ViewProps } from 'react-native';

import { ReorderRail } from '@sg/shared-fe-features-menu';

import { MenuReorderRailCard } from './components/MenuReorderRailCard';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuReorderRail = (props: Props) => {
  return (
    <ReorderRail.Container>
      <ReorderRail.Header>
        <ReorderRail.Heading />

        <MenuReorderRailSeeAll {...props} />
      </ReorderRail.Header>

      <MenuReorderRailList {...props} />
    </ReorderRail.Container>
  );
};

// ─── Compound Components ─────────────────────────────────────────────────────

const MenuReorderRailList = (props: Props & Pick<ViewProps, 'style'>) => {
  const {
    location,
    lineItems,
    mostRecentOrder,
    restaurantId,
    restaurantName,
    restaurantSlug,
    deliveryDetails,
    style,
  } = props;

  // ─── Derived ─────────────────────────────────────────────────────────

  const deliveryOrderDetails = useMemo(() => {
    if (!deliveryDetails) return;

    return {
      tip: deliveryDetails.tip,
      deliveryFee: deliveryDetails.deliveryFee,
      vendor: deliveryDetails.vendor,
      vendorRestaurantId: deliveryDetails.vendorRestaurantId,
      address: { id: deliveryDetails.addressId },
    };
  }, [deliveryDetails]);

  const targetRestaurant = useMemo(
    () => ({ restaurantId, restaurantName, restaurantSlug, deliveryOrderDetails }),
    [restaurantId, restaurantName, restaurantSlug, deliveryOrderDetails],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ReorderRail.List style={style}>
      {lineItems.map((lineItem) => (
        <MenuReorderRailCard
          key={lineItem.id}
          location={location}
          lineItem={lineItem}
          mostRecentOrder={mostRecentOrder}
          targetRestaurant={targetRestaurant}
        />
      ))}
    </ReorderRail.List>
  );
};

const MenuReorderRailSeeAll = (props: Pick<Props, 'restaurantId' | 'deliveryDetails'>) => {
  const { restaurantId, deliveryDetails } = props;

  // ─── Derived ─────────────────────────────────────────────────────────

  const reorderModalParams = useMemo(
    () => ({
      restaurantId,
      ...(deliveryDetails ? { addressId: deliveryDetails.addressId } : {}),
    }),
    [restaurantId, deliveryDetails],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Link href={{ pathname: '/reorder', params: reorderModalParams }} asChild>
      <Pressable accessibilityRole="link">
        <ReorderRail.SeeAll />
      </Pressable>
    </Link>
  );
};

MenuReorderRail.List = MenuReorderRailList;
MenuReorderRail.SeeAll = MenuReorderRailSeeAll;

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  location?: 'home' | 'menu';
  lineItems: MenuReorderRailCardProps['lineItem'][];
  mostRecentOrder: MenuReorderRailCardProps['mostRecentOrder'];
  restaurantId: string;
  restaurantName: string;
  restaurantSlug: string;
  deliveryDetails?: {
    addressId: string;
    tip: number;
    deliveryFee: number;
    vendor: string;
    vendorRestaurantId: string;
  };
};

type MenuReorderRailCardProps = ComponentProps<typeof MenuReorderRailCard>;
